// v1.0.0 - 2026-01-27 - GSE Excel Parser entry point

import type {
  GSERawCorrispettivi,
  GSERawRiepilogo,
  ParsedMisuraOraria,
  AggregatedMemberEnergy,
  ParseResult,
  MultiFileParseResult,
} from './gse-types'
import { readWorkbook, readWorkbookFromFile, getSheetNames, getSheet, detectSheetType } from './xlsx-utils'
import { parseCorrispettiviSheet, extractCodiceContratto } from './corrispettivi-parser'
import { parseRiepilogoSheet, aggregateRiepilogoByPOD } from './riepilogo-parser'
import { parseCurvaAuto, getCurvaStats } from './curva-parser'
import { aggregateByPOD, calculateEACI } from './aggregators'
import { crossValidateEnergy } from './validators'

// Re-export types
export type {
  GSERawCorrispettivi,
  GSERawRiepilogo,
  ParsedMisuraOraria,
  AggregatedMemberEnergy,
  ParseResult,
  MultiFileParseResult,
}

// Re-export utilities
export { readWorkbook, readWorkbookFromFile }
export { aggregateByPOD, aggregateRiepilogoByPOD, calculateEACI }
export { getCurvaStats }

/**
 * Parse a single GSE file and auto-detect content type
 */
export async function parseGSEFile(
  file: File
): Promise<{
  type: 'CORRISPETTIVI' | 'MISURE' | 'RIEPILOGO' | 'UNKNOWN'
  corrispettivi: ParseResult<GSERawCorrispettivi[]> | null
  misure: ParseResult<ParsedMisuraOraria[]> | null
  riepilogo: ParseResult<GSERawRiepilogo[]> | null
}> {
  const workbook = await readWorkbookFromFile(file)
  const sheetNames = getSheetNames(workbook)

  let corrispettivi: ParseResult<GSERawCorrispettivi[]> | null = null
  let misure: ParseResult<ParsedMisuraOraria[]> | null = null
  let riepilogo: ParseResult<GSERawRiepilogo[]> | null = null
  let detectedType: 'CORRISPETTIVI' | 'MISURE' | 'RIEPILOGO' | 'UNKNOWN' = 'UNKNOWN'

  for (const sheetName of sheetNames) {
    const sheet = getSheet(workbook, sheetName)
    if (!sheet) continue

    const type = detectSheetType(sheetName)

    switch (type) {
      case 'CORRISPETTIVI':
        corrispettivi = parseCorrispettiviSheet(sheet)
        detectedType = 'CORRISPETTIVI'
        break
      case 'CURVA':
        // Try to detect IMMESSA vs PRELEVATA from filename or sheet name
        const tipo = detectEnergyType(file.name, sheetName)
        misure = parseCurvaAuto(sheet, tipo)
        detectedType = 'MISURE'
        break
      case 'RIEPILOGO':
        riepilogo = parseRiepilogoSheet(sheet)
        detectedType = 'RIEPILOGO'
        break
    }
  }

  return {
    type: detectedType,
    corrispettivi,
    misure,
    riepilogo,
  }
}

/**
 * Parse multiple GSE files and aggregate results
 */
export async function parseMultipleGSEFiles(
  files: File[]
): Promise<MultiFileParseResult> {
  const result: MultiFileParseResult = {
    corrispettivi: null,
    misure: null,
    riepilogo: null,
    aggregated: null,
  }

  const allCorrispettivi: GSERawCorrispettivi[] = []
  const allMisure: ParsedMisuraOraria[] = []
  const allRiepilogo: GSERawRiepilogo[] = []
  const errors: string[] = []
  const warnings: string[] = []

  for (const file of files) {
    const parsed = await parseGSEFile(file)

    if (parsed.corrispettivi?.data) {
      allCorrispettivi.push(...parsed.corrispettivi.data)
      errors.push(...(parsed.corrispettivi.errors || []).map((e) => `${file.name}: ${e}`))
      warnings.push(...(parsed.corrispettivi.warnings || []).map((w) => `${file.name}: ${w}`))
    }

    if (parsed.misure?.data) {
      allMisure.push(...parsed.misure.data)
      errors.push(...(parsed.misure.errors || []).map((e) => `${file.name}: ${e}`))
      warnings.push(...(parsed.misure.warnings || []).map((w) => `${file.name}: ${w}`))
    }

    if (parsed.riepilogo?.data) {
      allRiepilogo.push(...parsed.riepilogo.data)
      errors.push(...(parsed.riepilogo.errors || []).map((e) => `${file.name}: ${e}`))
      warnings.push(...(parsed.riepilogo.warnings || []).map((w) => `${file.name}: ${w}`))
    }
  }

  // Build result
  if (allCorrispettivi.length > 0) {
    result.corrispettivi = {
      success: true,
      data: allCorrispettivi,
      errors: errors.filter((e) => e.includes('corrispettivi')),
      warnings: warnings.filter((w) => w.includes('corrispettivi')),
      rowCount: allCorrispettivi.length,
      skippedRows: 0,
    }
  }

  if (allMisure.length > 0) {
    result.misure = {
      success: true,
      data: allMisure,
      errors: errors.filter((e) => !e.includes('corrispettivi') && !e.includes('riepilogo')),
      warnings: warnings.filter((w) => !w.includes('corrispettivi') && !w.includes('riepilogo')),
      rowCount: allMisure.length,
      skippedRows: 0,
    }

    // Aggregate by POD
    const aggregated = aggregateByPOD(allMisure)
    result.aggregated = Array.from(aggregated.values())
  }

  if (allRiepilogo.length > 0) {
    result.riepilogo = {
      success: true,
      data: allRiepilogo,
      errors: errors.filter((e) => e.includes('riepilogo')),
      warnings: warnings.filter((w) => w.includes('riepilogo')),
      rowCount: allRiepilogo.length,
      skippedRows: 0,
    }
  }

  // Cross-validate if we have both riepilogo and misure
  if (result.riepilogo?.data && result.misure?.data) {
    const validation = crossValidateEnergy(
      result.riepilogo.data,
      result.misure.data
    )
    if (validation.warnings.length > 0) {
      result.misure.warnings.push(...validation.warnings)
    }
  }

  return result
}

/**
 * Parse GSE files from ArrayBuffers (for server-side processing)
 */
export function parseGSEBuffer(
  buffer: ArrayBuffer,
  filename: string
): {
  type: 'CORRISPETTIVI' | 'MISURE' | 'RIEPILOGO' | 'UNKNOWN'
  corrispettivi: ParseResult<GSERawCorrispettivi[]> | null
  misure: ParseResult<ParsedMisuraOraria[]> | null
  riepilogo: ParseResult<GSERawRiepilogo[]> | null
} {
  const workbook = readWorkbook(buffer)
  const sheetNames = getSheetNames(workbook)

  let corrispettivi: ParseResult<GSERawCorrispettivi[]> | null = null
  let misure: ParseResult<ParsedMisuraOraria[]> | null = null
  let riepilogo: ParseResult<GSERawRiepilogo[]> | null = null
  let detectedType: 'CORRISPETTIVI' | 'MISURE' | 'RIEPILOGO' | 'UNKNOWN' = 'UNKNOWN'

  for (const sheetName of sheetNames) {
    const sheet = getSheet(workbook, sheetName)
    if (!sheet) continue

    const type = detectSheetType(sheetName)

    switch (type) {
      case 'CORRISPETTIVI':
        corrispettivi = parseCorrispettiviSheet(sheet)
        detectedType = 'CORRISPETTIVI'
        break
      case 'CURVA':
        const tipo = detectEnergyType(filename, sheetName)
        misure = parseCurvaAuto(sheet, tipo)
        detectedType = 'MISURE'
        break
      case 'RIEPILOGO':
        riepilogo = parseRiepilogoSheet(sheet)
        detectedType = 'RIEPILOGO'
        break
    }
  }

  return {
    type: detectedType,
    corrispettivi,
    misure,
    riepilogo,
  }
}

/**
 * Detect energy type (IMMESSA/PRELEVATA) from filename or sheet name
 */
function detectEnergyType(
  filename: string,
  sheetName: string
): 'IMMESSA' | 'PRELEVATA' {
  const combined = `${filename} ${sheetName}`.toLowerCase()

  if (combined.includes('immessa') || combined.includes('produzione') || combined.includes('prod')) {
    return 'IMMESSA'
  }

  if (combined.includes('prelevata') || combined.includes('consumo') || combined.includes('cons')) {
    return 'PRELEVATA'
  }

  // Default to IMMESSA for production data (most common GSE format)
  return 'IMMESSA'
}

/**
 * Get summary of parsed data
 */
export function getParseResultSummary(result: MultiFileParseResult): {
  corrispettivi: { months: number; totalTIP: number; totalEACI: number } | null
  misure: { records: number; pods: number; days: number } | null
  riepilogo: { records: number; pods: number } | null
  codiceContratto: string | null
} {
  let corrispettiviSummary = null
  let misureSummary = null
  let riepilogoSummary = null
  let codiceContratto: string | null = null

  if (result.corrispettivi?.data) {
    const data = result.corrispettivi.data
    corrispettiviSummary = {
      months: data.length,
      totalTIP: data.reduce((sum, r) => sum + r.tip_eur, 0),
      totalEACI: data.reduce((sum, r) => sum + r.eaci_kwh, 0),
    }
    codiceContratto = extractCodiceContratto(data)
  }

  if (result.misure?.data) {
    const stats = getCurvaStats(result.misure.data)
    misureSummary = {
      records: stats.records,
      pods: stats.pod_count,
      days: stats.days,
    }
  }

  if (result.riepilogo?.data) {
    const data = result.riepilogo.data
    riepilogoSummary = {
      records: data.length,
      pods: new Set(data.map((r) => r.pod)).size,
    }
  }

  return {
    corrispettivi: corrispettiviSummary,
    misure: misureSummary,
    riepilogo: riepilogoSummary,
    codiceContratto,
  }
}
